import i18n from '../../i18n/index.js';
import { db, kvGet, kvSet } from './db.js';

/**
 * Offline user switching (Settings → Offline & displays → "Switch users offline").
 *
 * When someone signs in with their PIN on this device while online, the device keeps their
 * profile and a check value for the PIN: PBKDF2-SHA256 with a random salt and 600,000 rounds
 * (OWASP's figure), never the PIN itself. Offline, the PIN screen offers the people who signed
 * in here within the last `userSwitchDays` days and checks the PIN against that value. Five
 * wrong PINs lock that person on this device for 5 minutes. The check is dropped when the
 * person's PIN changes, they are switched off, or they lose access to this outlet (the staff list
 * is refreshed while online).
 *
 * Back online, the app signs in for real with the PIN typed offline (kept in memory only), so
 * the server session matches the person at the till.
 */
const ITERATIONS = 600_000;
const MAX_FAILS = 5;
const LOCK_MS = 5 * 60_000;
const enc = new TextEncoder();
const hex = (buf) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
const fromHex = (h) => Uint8Array.from(h.match(/../g).map((b) => parseInt(b, 16)));

async function derive(pin, userId, salt, iterations) {
  const key = await crypto.subtle.importKey('raw', enc.encode(`${userId}:${pin}`), 'PBKDF2', false, ['deriveBits']);
  return hex(await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, key, 256));
}

/** Same length, every character compared (no early exit on the first difference). */
function equal(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** After an online PIN sign-in on this device: remember the person for offline switching. */
export async function rememberPinSignIn(me, pin) {
  try {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const hash = await derive(pin, me.user.id, salt, ITERATIONS);
    const staff = (await kvGet('staff')) ?? [];
    await db.staff.put({
      userId: me.user.id,
      me,
      verifier: { salt: hex(salt), hash, iterations: ITERATIONS },
      pinSetAt: staff.find((s) => s.id === me.user.id)?.pinSetAt ?? null,
      signedInAt: Date.now(),
      fails: 0,
      lockedUntil: 0,
    });
  } catch {
    /* no WebCrypto / storage: offline switching just isn't available for this person */
  }
}

/** The staff list from the server (PIN screen): cache it and drop checks that no longer apply. */
export async function refreshOfflineStaff(items) {
  await kvSet('staff', items);
  try {
    const byId = new Map(items.map((s) => [s.id, s]));
    for (const row of await db.staff.toArray()) {
      const s = byId.get(row.userId);
      const pinChanged = s && (s.pinSetAt ?? null) !== (row.pinSetAt ?? null) && row.pinSetAt !== null;
      if (!s || pinChanged) await db.staff.delete(row.userId);
      else if (row.pinSetAt === null && s.pinSetAt) await db.staff.update(row.userId, { pinSetAt: s.pinSetAt });
    }
  } catch {
    /* ignore */
  }
}

/** Staff for the offline PIN screen: everyone on the cached list, with who can switch in. */
export async function offlineStaff(switchDays) {
  const items = (await kvGet('staff')) ?? [];
  let rows = [];
  try {
    rows = await db.staff.toArray();
  } catch {
    /* none */
  }
  const fresh = new Map(
    rows.filter((r) => Date.now() - r.signedInAt <= switchDays * 86_400_000).map((r) => [r.userId, r]),
  );
  return items.map((s) => ({ ...s, canSwitch: fresh.has(s.id) }));
}

/**
 * Check a PIN offline. Returns the person's profile, or throws an Error with a message for the
 * PIN screen (wrong PIN, locked, not available).
 */
export async function verifyOfflinePin(userId, pin, switchDays) {
  const row = await db.staff.get(userId).catch(() => null);
  if (!row || Date.now() - row.signedInAt > switchDays * 86_400_000)
    throw new Error(i18n.t('pin.signInOnceFirst', 'Sign in once with internet on this device first.'));
  if (row.lockedUntil > Date.now()) {
    const mins = Math.ceil((row.lockedUntil - Date.now()) / 60_000);
    throw Object.assign(new Error(i18n.t('pin.locked', 'Too many wrong PINs. Try again in {{mins}} min.', { mins })), {
      locked: true,
    });
  }
  const { salt, hash, iterations } = row.verifier;
  const ok = equal(await derive(pin, userId, fromHex(salt), iterations), hash);
  if (!ok) {
    const fails = (row.fails ?? 0) + 1;
    await db.staff.update(userId, { fails, lockedUntil: fails >= MAX_FAILS ? Date.now() + LOCK_MS : 0 });
    if (fails >= MAX_FAILS)
      throw Object.assign(
        new Error(i18n.t('pin.locked', 'Too many wrong PINs. Try again in {{mins}} min.', { mins: 5 })),
        {
          locked: true,
        },
      );
    throw Object.assign(new Error('Incorrect PIN.'), { wrongPin: true });
  }
  await db.staff.update(userId, { fails: 0, lockedUntil: 0 });
  return row.me;
}

// ---------------------------------------------------------------- back online
let pending = null; // { userId, pin }: memory only, gone on reload

export function rememberForReconnect(userId, pin) {
  pending = { userId, pin };
}

export function takeReconnectPin() {
  const p = pending;
  pending = null;
  return p;
}

export async function markOfflineSession(on) {
  await kvSet('offlineSession', on ? { at: Date.now() } : null);
}

export async function wasOfflineSession() {
  return Boolean(await kvGet('offlineSession'));
}
